import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const stats = [
  { key: 'clients', value: 500, suffix: '+', label: 'Clientes Satisfaites', icon: 'ri-user-heart-line' },
  { key: 'experience', value: 5, suffix: '', label: "Années d'Expertise", icon: 'ri-award-line' },
  { key: 'treatments', value: 15, suffix: '+', label: 'Soins Proposés', icon: 'ri-sparkling-2-line' },
  { key: 'rating', value: 47, suffix: '/5', label: 'Note Moyenne', decimal: true, icon: 'ri-star-smile-line' },
];

function AnimatedCounter({ target, suffix, decimal, duration = 2000 }: { target: number; suffix: string; decimal?: boolean; duration?: number }) {
  const [count, setCount] = useState(0);
  const [started, setStarted] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !started) {
          setStarted(true);
        }
      },
      { threshold: 0.5 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [started]);

  useEffect(() => {
    if (!started) return;
    let startTime: number | null = null;
    let rafId: number;

    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3); // easeOutCubic
      setCount(Math.floor(eased * target));
      if (progress < 1) {
        rafId = requestAnimationFrame(animate);
      }
    };

    rafId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafId);
  }, [started, target, duration]);

  const display = decimal ? `${(count / 10).toFixed(1)}` : count.toLocaleString('fr-FR');

  return (
    <div ref={ref} className="font-heading text-4xl md:text-5xl text-primary-600 mb-2">
      {display}{suffix}
    </div>
  );
}

export default function StatsSection() {
  const { t } = useTranslation();
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const elements = entry.target.querySelectorAll('.stat-reveal');
            elements.forEach((el, i) => {
              setTimeout(() => { el.classList.add('visible'); }, i * 120);
            });
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.3 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="w-full py-14 md:py-20 bg-background-50 border-y border-background-200/40 relative overflow-hidden">
      {/* Subtle decorative bg */}
      <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: `radial-gradient(circle at 25% 50%, oklch(var(--primary-500)) 1px, transparent 1px), radial-gradient(circle at 75% 50%, oklch(var(--primary-500)) 1px, transparent 1px)`,
        backgroundSize: '60px 60px',
      }} />

      <div className="w-full px-4 md:px-8 lg:px-12 relative z-10">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 md:gap-4 max-w-5xl mx-auto">
          {stats.map((stat) => (
            <div
              key={stat.key}
              className="stat-reveal opacity-0 translate-y-4 transition-all duration-700 text-center flex flex-col items-center"
              style={{ transitionTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)' }}
            >
              <div className="w-14 h-14 mb-4 flex items-center justify-center rounded-2xl bg-primary-50 border border-primary-100/60">
                <div className="w-7 h-7 flex items-center justify-center">
                  <i className={`${stat.icon} text-2xl text-primary-500`} />
                </div>
              </div>
              <AnimatedCounter target={stat.value} suffix={stat.suffix} decimal={stat.decimal} />
              <p className="text-sm md:text-base text-foreground-600 font-body">
                {stat.label}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}